import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Download, 
  File, 
  FileText, 
  Image, 
  FileSpreadsheet,
  Loader2, 
  CheckCircle, 
  XCircle,
  Search,
  Calendar,
  HardDrive
} from 'lucide-react';
import { 
  downloadFileById, 
  downloadFileByPath, 
  getSubmissionFiles,
  getFileInfo,
  downloadSubmissionFilesAsZip
} from '@/lib/file-download';
import { supabase } from '@/integrations/supabase/client';

interface FileRecord {
  id: string;
  submission_id: string | null;
  file_path: string;
  doc_type: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  uploaded_at: string | null;
}

interface DownloadStatus {
  status: 'idle' | 'loading' | 'success' | 'error';
  message?: string;
}

export function FileManagementPanel() {
  const [files, setFiles] = useState<FileRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [downloadStatuses, setDownloadStatuses] = useState<Record<string, DownloadStatus>>({});

  // Load all uploaded files
  const loadFiles = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('uploads')
        .select('*')
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      setFiles(data || []);
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, []);

  // Update download status
  const updateDownloadStatus = (fileId: string, status: DownloadStatus) => {
    setDownloadStatuses(prev => ({ ...prev, [fileId]: status }));
  };

  // Handle file download
  const handleDownload = async (file: FileRecord) => {
    updateDownloadStatus(file.id, { status: 'loading' });
    
    try {
      const result = await downloadFileById(file.id);
      if (result.success) {
        updateDownloadStatus(file.id, { status: 'success', message: result.message });
        // Reset status after 3 seconds
        setTimeout(() => {
          updateDownloadStatus(file.id, { status: 'idle' });
        }, 3000);
      } else {
        updateDownloadStatus(file.id, { status: 'error', message: result.error });
      }
    } catch (error) {
      updateDownloadStatus(file.id, { 
        status: 'error', 
        message: error instanceof Error ? error.message : 'Download failed' 
      });
    }
  };

  // Get file icon based on mime type
  const getFileIcon = (mimeType: string | null) => {
    if (!mimeType) return <File className="h-5 w-5 text-gray-500" />;
    
    if (mimeType.startsWith('image/')) {
      return <Image className="h-5 w-5 text-blue-500" />;
    } else if (mimeType.includes('pdf')) {
      return <FileText className="h-5 w-5 text-red-500" />;
    } else if (mimeType.includes('sheet') || mimeType.includes('excel')) {
      return <FileSpreadsheet className="h-5 w-5 text-green-500" />;
    } else {
      return <File className="h-5 w-5 text-gray-500" />;
    }
  };

  // Format file size
  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  // Filter files based on search term
  const filteredFiles = files.filter(file => 
    file.doc_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    file.file_path.toLowerCase().includes(searchTerm.toLowerCase()) ||
    file.mime_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get download status
  const getDownloadStatus = (fileId: string) => {
    return downloadStatuses[fileId] || { status: 'idle' };
  };

  return (
    <Card className="w-full max-w-6xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <HardDrive className="h-5 w-5" />
          File Management Panel
        </CardTitle>
        <CardDescription>
          View and download uploaded files from your business insurance submissions
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Search and Controls */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Label htmlFor="search" className="sr-only">Search files</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="search"
                placeholder="Search by document type, file path, or mime type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Button onClick={loadFiles} disabled={loading} variant="outline">
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              'Refresh'
            )}
          </Button>
        </div>

        {/* Files Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <h3 className="font-medium text-blue-900">Total Files</h3>
            <p className="text-2xl font-bold text-blue-700">{files.length}</p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg">
            <h3 className="font-medium text-green-900">Filtered Results</h3>
            <p className="text-2xl font-bold text-green-700">{filteredFiles.length}</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <h3 className="font-medium text-purple-900">Total Size</h3>
            <p className="text-2xl font-bold text-purple-700">
              {formatFileSize(files.reduce((sum, file) => sum + (file.size_bytes || 0), 0))}
            </p>
          </div>
        </div>

        {/* Files List */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
              <p>Loading files...</p>
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? 'No files match your search criteria' : 'No files uploaded yet'}
            </div>
          ) : (
            filteredFiles.map((file) => {
              const downloadStatus = getDownloadStatus(file.id);
              return (
                <div key={file.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center gap-3 flex-1">
                    {getFileIcon(file.mime_type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium truncate">
                          {file.doc_type || 'Unknown Document'}
                        </h3>
                        <Badge variant="outline" className="text-xs">
                          {file.mime_type?.split('/')[1]?.toUpperCase() || 'UNKNOWN'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 truncate">
                        Path: {file.file_path}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                        <span className="flex items-center gap-1">
                          <HardDrive className="h-3 w-3" />
                          {formatFileSize(file.size_bytes)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(file.uploaded_at)}
                        </span>
                      </div>
                      {downloadStatus.message && (
                        <p className={`text-xs mt-1 ${
                          downloadStatus.status === 'error' ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {downloadStatus.message}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {downloadStatus.status === 'success' && (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    )}
                    {downloadStatus.status === 'error' && (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    <Button
                      onClick={() => handleDownload(file)}
                      disabled={downloadStatus.status === 'loading'}
                      size="sm"
                      variant="outline"
                    >
                      {downloadStatus.status === 'loading' ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Instructions */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">📁 File Management Instructions:</h4>
          <div className="text-sm text-blue-800 space-y-1">
            <p>• <strong>Download:</strong> Click the download button to save files to your computer</p>
            <p>• <strong>Search:</strong> Use the search box to filter files by type or name</p>
            <p>• <strong>File Types:</strong> Supports PDFs, images, Excel files, and documents</p>
            <p>• <strong>Storage:</strong> Files are securely stored in your private Supabase bucket</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
